import React, { useEffect } from 'react'
import { useField } from 'formik'
import { groupBy, sortBy } from 'lodash'
import { Form } from 'react-bootstrap'
import Select, { components } from 'react-select'
import styled from 'styled-components'
import { CheckboxInSelect, FormGroup } from 'components'
import InfoTooltip from 'components/InfoTooltip'
import Icon from 'components/Icon'
import { _, colors } from 'utils'
import { SelectFieldProps } from '../types'

const S = {
    FormGroup: styled(FormGroup)<{ showInfoIcon: boolean }>`
        position: relative;
    `,
    InfoTooltip: styled(InfoTooltip)`
        position: absolute;
        top: 33px;
        right: 48px;
    `,
    GroupLabel: styled.div`
        font-size: 12px;
        font-weight: 700;
        text-transform: uppercase;
        color: ${colors.iconOpacity(0.5)};
        margin-top: 8px;
    `,
    OptionWrapper: styled.div`
        display: flex;
        align-items: center;
        justify-content: space-between;
    `,
    ColorIndicator: styled.div`
        flex-shrink: 0;
        width: 16px;
        height: 16px;
        border-radius: 2px;
    `
}

const DropdownIcon = styled(Icon)`
    margin-left: -5px;
    margin-right: 2px;
    display: flex;
    align-items: center;
`
const DropdownIndicator = (props) => (
    <components.DropdownIndicator {...props}>
        <DropdownIcon icon="menuDown" color="#474A4C" size={18} />
    </components.DropdownIndicator>
)
const Option = (props: any) => (
    <div>
        <components.Option {...props}>
            {props.isDisabled ? (
                <S.GroupLabel>{props.label}</S.GroupLabel>
            ) : (
                <S.OptionWrapper>
                    <CheckboxInSelect label={props.label} checked={props.isSelected} />
                    <S.ColorIndicator style={{ background: props.data?.color || 'transparent' }} />
                </S.OptionWrapper>
            )}
        </components.Option>
    </div>
)

const SelectField = ({
    name,
    label,
    helperText,
    isRequired,
    isMulti,
    isClearable = true,
    options,
    optionValue = 'value',
    optionLabel = 'label',
    onSelect = () => null,
    isDisabled,
    placeholder,
    defaultValue,
    showInfoIcon,
    ...props
}: SelectFieldProps) => {
    const [
        field,
        meta,
        helpers
    ] = useField({ name })
    const { error, touched } = meta

    const customStyles = {
        control: (provided, { isDisabled: _isDisabled }) => ({
            ...provided,
            height: 44,
            fontSize: 14,
            borderColor: error && touched ? colors.danger : colors.inputBorder,
            background: 'transparent',
            opacity: _isDisabled ? 0.5 : 1,
        }),
        indicatorSeparator: (provided) => ({
            ...provided,
            height: 12,
            margin: 'auto',
            background: colors.inputBorder,
            marginLeft: showInfoIcon ? 20 : 0
        }),
        singleValue: (provided) => ({
            ...provided,
            fontSize: 14,
            color: colors.grayTitle,
        }),
        option: (provided) => ({
            ...provided,
            fontSize: 14,
            backgroundColor: isMulti ? 'white !important' : provided.backgroundColor,
            color: isMulti ? `${colors.icon} !important` : provided.color,
        }),
        placeholder: (provided) => ({
            ...provided,
            fontWeight: 400,
            fontSize: '14px',
            color: colors.grayTitleOpacity(0.4),
        }),
        valueContainer: (provided) => ({
            ...provided,
            maxHeight: 42,
            overflowX: 'auto',
            overflowY: 'hidden',
            flexWrap: 'no-wrap',
        }),
        multiValue: (provided) => ({
            ...provided,
            minWidth: 60,
            background: 'rgba(24, 28, 33, 0.15)',
            borderRadius: '4px',
            fontSize: '14px',
            color: colors.grayTitle,
        }),
        multiValueLabel: (provided) => ({
            ...provided,
            padding: '5px 2px',
            flex: 1,
        }),
        multiValueRemove: (provided) => ({
            ...provided,
            padding: 2,
        }),
    }

    useEffect(() => {
        if (field.value) {
            // When changing value, update the selected data so other places can get it
            onSelect(options?.find((option) => option[optionValue] === field.value))
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [field.value, optionValue, options])

    useEffect(() => {
        if (defaultValue && !field.value) {
            helpers.setValue(defaultValue[optionValue])
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const getValue = () => {
        if (!field.value) {
            return ''
        }
        return isMulti
            ? options.filter((option) => field.value.includes(option[optionValue]))
            : options.find((option) => option[optionValue] === field.value) || ''
    }

    const handleChange = (option, triggeredAction) => {
        if (triggeredAction.action === 'clear') {
            helpers.setValue(null)
            onSelect({
                [optionValue]: null,
                [optionLabel]: ''
            })
            return
        }
        if (isMulti) {
            helpers.setValue((option || []).map((e) => e[optionValue]))
        } else {
            helpers.setValue(option[optionValue])
        }
    }

    let _components = { DropdownIndicator }
    if (isMulti) {
        // @ts-ignore
        _components = { ..._components, Option }
    }

    return (
        <S.FormGroup
            controlId={`${field.name}-select-field`}
            {...props}
        >
            <Form.Label className={isDisabled ? 'disabled-label' : ''}>
                {label}
                {isRequired && <span>&nbsp;*</span>}
            </Form.Label>
            <div className="matching-arrow" />
            <Select
                classNamePrefix="react-select"
                className="select-field"
                options={options}
                {...field}
                onChange={handleChange}
                value={getValue()}
                onBlur={() => helpers.setTouched(true)}
                styles={customStyles}
                isMulti={isMulti}
                isClearable={isClearable}
                getOptionValue={(option) => option[optionValue]}
                getOptionLabel={(option) => option[optionLabel]}
                components={_components}
                closeMenuOnSelect={!isMulti}
                hideSelectedOptions={false}
                blurInputOnSelect={!isMulti}
                placeholder={placeholder}
                isSearchable={!isMulti}
            />
            {showInfoIcon && <S.InfoTooltip size={22} text={helperText} />}

            {!(touched && error) && !showInfoIcon && (
                <Form.Text muted>
                    {helperText}
                </Form.Text>
            )}

            {touched && error && <small className="text-danger invalid-field">{_(error)}</small>}
        </S.FormGroup>
    )
}

export const convertGroupedTags = (tags = []) => {
    const options = []
    const groupedData = groupBy(tags, (e) => e.category?.text)
    const categories = sortBy(Object.entries(groupedData), (e) => e[0])
    categories.forEach((e) => {
        options.push({ text: e[0] === 'undefined' ? 'uncategorized' : e[0], isDisabled: true })
        options.push(...sortBy(e[1], 'text'))
    })
    return options
}

export default SelectField
